import { addDoc, collection, doc, getDoc, onSnapshot, orderBy, query, serverTimestamp, updateDoc, writeBatch } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { db, storage } from "../../../../backend/firebaseConfig";
import { assertChargeDatesAreOpen } from "../../../Accounts/MonthlyPayments/utils/closedChargeMonths";

const PRINTING_PATH = ["Marketing", "Printing", "Items"];
const PRINTING_CONFIG_PATH = ["Marketing", "Printing"];
const printingCollection = () => collection(db, ...PRINTING_PATH);
const printingAuditCollection = (id) => collection(db, ...PRINTING_PATH, id, "AuditLogs");
const trimValue = (value) => String(value ?? "").trim();
const actorData = (actor = {}) => ({ uid: actor.uid || "", email: actor.email || "", displayName: actor.displayName || "" });
const getChanges = (before, after) => Object.keys(after).reduce((changes, field) => {
  if (JSON.stringify(before?.[field] ?? null) !== JSON.stringify(after[field] ?? null)) changes[field] = { before: before?.[field] ?? null, after: after[field] ?? null };
  return changes;
}, {});
const addAuditLog = (id, action, actor, changes) => addDoc(printingAuditCollection(id), {
  action, actor: actorData(actor), changes, changedFields: Object.keys(changes), createdAt: serverTimestamp(), createdAtMs: Date.now(),
});

const uploadInvoice = async (printingId, file) => {
  if (!file || !(file instanceof File)) return null;
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
  const fileRef = ref(storage, `Marketing/Printing/${printingId}/invoices/${Date.now()}-${safeName}`);
  await uploadBytes(fileRef, file, { contentType: file.type });
  return {
    name: file.name,
    type: file.type || "application/octet-stream",
    size: file.size,
    storagePath: fileRef.fullPath,
    url: await getDownloadURL(fileRef),
  };
};

export const createPrintingCharge = async ({ formData, invoice, actor }) => {
  const formPayload = buildPrintingPayload(formData);
  await assertChargeDatesAreOpen([formPayload.orderDate]);
  const payload = {
    ...formPayload, invoice: null,
    createdBy: actorData(actor),
    createdAt: serverTimestamp(),
    createdAtMs: Date.now(),
  };
  const chargeRef = await addDoc(printingCollection(), payload);
  const uploadedInvoice = await uploadInvoice(chargeRef.id, invoice);
  if (uploadedInvoice) {
    await updateDoc(chargeRef, { invoice: uploadedInvoice });
  }
  await addAuditLog(chargeRef.id, "created", actor, getChanges({}, { ...formPayload, invoice: uploadedInvoice }));
  return chargeRef.id;
};

const buildPrintingPayload = (formData) => ({
  orderDate: trimValue(formData.orderDate),
  adviserName: trimValue(formData.adviserName),
  adviserEmail: trimValue(formData.adviserEmail).toLowerCase(),
  supplier: trimValue(formData.supplier),
  orderItems: Array.isArray(formData.orderItems)
    ? formData.orderItems.map(trimValue).filter(Boolean)
    : [],
  orderNumber: trimValue(formData.orderNumber),
  orderDescription: trimValue(formData.orderDescription),
  totalPaid: trimValue(formData.totalPaid),
  markup: trimValue(formData.markup) || "20",
  amountToCharge: trimValue(formData.amountToCharge),
  chargeAcross: trimValue(formData.chargeAcross),
});

export const updatePrintingCharge = async ({ printingId, formData, invoice, actor }) => {
  const chargeRef = doc(db, ...PRINTING_PATH, printingId);
  const currentSnapshot = await getDoc(chargeRef);
  if (!currentSnapshot.exists()) throw new Error("Printing charge not found.");
  const formPayload = buildPrintingPayload(formData);
  await assertChargeDatesAreOpen([
    currentSnapshot.data().orderDate,
    formPayload.orderDate,
  ]);
  const uploadedInvoice = invoice instanceof File
    ? await uploadInvoice(printingId, invoice)
    : invoice || null;

  const next = { ...formPayload, invoice: uploadedInvoice };
  const changes = getChanges(currentSnapshot.data(), next);
  if (!Object.keys(changes).length) return;
  await updateDoc(chargeRef, {
    ...next,
    updatedBy: {
      uid: actor?.uid || "",
      email: actor?.email || "",
      displayName: actor?.displayName || "",
    },
    updatedAt: serverTimestamp(),
    updatedAtMs: Date.now(),
  });
  await addAuditLog(printingId, "updated", actor, changes);
};

export const importPrintingCharges = async ({ charges, actor }) => {
  await assertChargeDatesAreOpen(charges.map((charge) => charge.orderDate));
  const importedAtMs = Date.now();
  const batchSize = 240;

  for (let start = 0; start < charges.length; start += batchSize) {
    const batch = writeBatch(db);
    charges.slice(start, start + batchSize).forEach((charge, index) => {
      const chargeRef = doc(printingCollection());
      const formPayload = buildPrintingPayload(charge);
      const createdAtMs = importedAtMs + start + index;
      batch.set(chargeRef, {
        ...formPayload,
        invoice: null,
        createdBy: actorData(actor),
        importedFromExcel: true,
        createdAt: serverTimestamp(),
        createdAtMs,
      });
      batch.set(doc(printingAuditCollection(chargeRef.id)), {
        action: "imported",
        actor: actorData(actor),
        changes: getChanges({}, { ...formPayload, invoice: null }),
        changedFields: Object.keys(formPayload),
        createdAt: serverTimestamp(),
        createdAtMs,
      });
    });
    await batch.commit();
  }

  return charges.length;
};

export const subscribePrintingCharges = (callback, onError) =>
  onSnapshot(
    query(printingCollection(), orderBy("createdAtMs", "desc")),
    (snapshot) => callback(snapshot.docs.map((item) => ({ id: item.id, ...item.data() }))),
    onError,
  );

export const subscribePrintingCharge = (printingId, callback, onError) =>
  onSnapshot(
    doc(db, ...PRINTING_PATH, printingId),
    (snapshot) => callback(snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null),
    onError,
  );

export const subscribePrintingAuditLogs = (printingId, callback, onError) => onSnapshot(
  query(printingAuditCollection(printingId), orderBy("createdAtMs", "desc")),
  (snapshot) => callback(snapshot.docs.map((item) => ({ id: item.id, ...item.data() }))), onError,
);

export const subscribePrintingOptions = (callback, onError) =>
  onSnapshot(
    doc(db, ...PRINTING_CONFIG_PATH),
    (snapshot) => {
      const data = snapshot.exists() ? snapshot.data() : {};
      callback({
        orderItems: Array.isArray(data.orderItems)
          ? data.orderItems.map(trimValue).filter(Boolean)
          : [],
        suppliers: Array.isArray(data.suppliers)
          ? data.suppliers.map(trimValue).filter(Boolean)
          : [],
      });
    },
    onError,
  );
